import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PlayCircle, FileText, Download, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/i18n";

// Videos whose embed_url is missing / obviously placeholder / from a non-whitelisted host
// are rendered as a "Coming Soon" card instead of a broken <iframe>.
const VALID_EMBED = /^https:\/\/(www\.)?(youtube\.com|youtube-nocookie\.com|drive\.google\.com|player\.vimeo\.com)\//i;
const isPlaceholderVideo = (url) => {
  if (!url || typeof url !== "string") return true;
  if (!VALID_EMBED.test(url)) return true;
  const low = url.toLowerCase();
  return low.includes("funny") || low.includes("rabbit") || low.includes("placeholder");
};

export default function ELearningPage() {
  const { t } = useI18n();
  const [videos, setVideos] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/videos"),
      api.get("/ebooks"),
      api.get("/content/elearning"),
    ])
      .then(([v, e, c]) => {
        setVideos(v.data);
        setEbooks(e.data);
        setContent(c.data);
      })
      .catch(() => toast.error("Gagal memuat konten"))
      .finally(() => setLoading(false));
  }, []);

  const c = (key, fallback = "") => content[key] || fallback;

  return (
    <main className="bg-[#FDFBF7] text-[#1C1917] min-h-screen" data-testid="elearning-page">
      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-14 md:pb-20 overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[280px] md:w-[440px] h-[280px] md:h-[440px] rounded-full bg-[#F39237]/15 blur-3xl pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="overline text-[#F39237] mb-4">{c("hero_overline", t("elearning.hero_overline", "Aurora Academy"))}</div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] text-[#0A0A0A] mb-5">
              {c("hero_title", t("elearning.hero_title", "Belajar bareng, tumbuh bareng."))}
            </h1>
            <p className="text-stone-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              {c("hero_subtitle", t("elearning.hero_subtitle", "Kumpulan video tutorial, materi pemasaran, dan e-book eksklusif khusus untuk mitra Aurora Hijab."))}
            </p>
          </motion.div>
        </div>
      </section>

      {/* VIDEOS */}
      <section className="py-16 md:py-24" data-testid="videos-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-10 md:mb-14 gap-4 flex-wrap">
            <div>
              <div className="overline text-[#F39237] mb-3">{t("elearning.videos_overline", "Video Edukasi")}</div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#0A0A0A]">
                {t("elearning.videos_title_a", "Video")}{" "}
                <em className="italic">{t("elearning.videos_title_em", "tutorial")}</em>{" "}
                {t("elearning.videos_title_b", "pilihan.")}
              </h2>
            </div>
            <div className="text-sm text-stone-500 flex items-center gap-2">
              <PlayCircle size={16} /> {videos.length} {t("elearning.videos_count", "video tersedia")}
            </div>
          </div>

          {loading ? (
            <div className="text-stone-500" data-testid="videos-loading">{t("elearning.loading", "Memuat…")}</div>
          ) : videos.length === 0 ? (
            <div className="text-stone-500 py-12 text-center border border-dashed border-stone-300">
              {t("elearning.empty_videos", "Belum ada video.")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10" data-testid="videos-grid">
              {videos.map((v, i) => {
                const isComingSoon = isPlaceholderVideo(v.embed_url);
                return (
                  <motion.article
                    key={v.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                    className="group transition-transform hover:-translate-y-1"
                    data-testid={`video-card-${v.id}`}
                  >
                    <div className="relative aspect-video bg-black overflow-hidden mb-4">
                      {isComingSoon ? (
                        <div
                          className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#0A0A0A] via-[#1a1a1a] to-[#0A0A0A] text-stone-100 gap-3 p-6 text-center"
                          data-testid={`video-coming-soon-${v.id}`}
                        >
                          <Clock size={32} className="text-[#F39237]" />
                          <div className="font-display text-xl text-white">
                            {t("elearning.coming_soon_title", "Coming Soon")}
                          </div>
                          <p className="text-xs text-stone-400 max-w-xs">
                            {t("elearning.coming_soon_desc", "Video sedang kami siapkan. Nantikan segera ya!")}
                          </p>
                        </div>
                      ) : (
                        <iframe
                          title={v.title}
                          src={v.embed_url}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      )}
                    </div>
                    <h3 className="font-display text-xl text-[#0A0A0A] mb-2">{v.title}</h3>
                    <p className="text-sm text-stone-600 leading-relaxed">{v.description}</p>
                  </motion.article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* EBOOKS */}
      <section className="py-16 md:py-24 bg-[#F5F2EB]" data-testid="ebooks-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-10 md:mb-14 gap-4 flex-wrap">
            <div>
              <div className="overline text-[#F39237] mb-3">{t("elearning.ebooks_overline", "E-Book & Materi")}</div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#0A0A0A]">
                {t("elearning.ebooks_title_a", "Materi")}{" "}
                <em className="italic">{t("elearning.ebooks_title_em", "download")}</em>
                {t("elearning.ebooks_title_b", ".")}
              </h2>
            </div>
            <div className="text-sm text-stone-500 flex items-center gap-2">
              <FileText size={16} /> {ebooks.length} {t("elearning.ebooks_count", "e-book")}
            </div>
          </div>

          {loading ? (
            <div className="text-stone-500">{t("elearning.loading", "Memuat…")}</div>
          ) : ebooks.length === 0 ? (
            <div className="text-stone-500 py-12 text-center border border-dashed border-stone-300 bg-white">
              {t("elearning.empty_ebooks", "Belum ada e-book.")}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-testid="ebooks-grid">
              {ebooks.map((e, i) => (
                <motion.article
                  key={e.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                  className="bg-white border border-stone-200 p-6 md:p-8 flex gap-5 md:gap-6 hover:-translate-y-1 hover:shadow-lg transition-all group"
                  data-testid={`ebook-card-${e.id}`}
                >
                  {e.thumbnail && (
                    <img
                      src={e.thumbnail}
                      alt={e.title}
                      className="w-24 md:w-32 h-32 md:h-40 object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex flex-col justify-between min-w-0">
                    <div>
                      <h3 className="font-display text-xl md:text-2xl text-[#0A0A0A] mb-2">{e.title}</h3>
                      <p className="text-sm text-stone-600 leading-relaxed mb-4">{e.description}</p>
                    </div>
                    <a
                      href={e.file_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`ebook-download-${e.id}`}
                      className="inline-flex items-center gap-2 text-sm text-[#0A0A0A] border-b border-[#0A0A0A] pb-1 w-fit hover:text-[#F39237] hover:border-[#F39237] hover:-translate-y-0.5 transition-all"
                    >
                      <Download size={14} /> {t("elearning.download_pdf", "Download PDF")}
                    </a>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
